<template>
  <div class="notification">
    <div class="notification-container">
      <div v-if="toastNotifications()">
        <notification-toast
          v-for="notification in toastNotifications()"
          :key="notification.id"
          :notification="notification"
        />
      </div>
    </div>
    <notification-modal
      v-for="notification in modalNotifications()"
      :key="notification.id"
      :notification="notification"
    >
    </notification-modal>
    <notification-modal
      v-for="notification in refreshNotifications()"
      :key="notification.id"
      :notification="notification"
    >
    </notification-modal>
    <system-notification-flow v-if="isRetailerPanel === true" />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import { useNotificationPermissionStore } from '@/stores/helpers/notificationPermission.js'
import {
  isSystemNotificationFlowItem,
  useSystemNotificationFlowStore
} from '@/stores/retailer/systemNotificationFlow.js'
import { NOTIFICATION_TYPES } from '@/constants/constants.js'

const defaultNotificationType = NOTIFICATION_TYPES.TOAST

// #region - route set
const route = useRoute()

// NotificationContainer is mounted globally; the system notification flow belongs to the retailer panel only.
const isRetailerPanel = computed(() => !route.path.startsWith('/admin'))

// endregion

// #region - importing components
import NotificationModal from '@/components/NotificationModal.vue'
import NotificationToast from '@/components/NotificationToast.vue'
import SystemNotificationFlow from '@/components/systemNotification/SystemNotificationFlow.vue'

// endregion

// #region - store init
const notificationStore = useNotificationStore()
const notificationPermissionStore = useNotificationPermissionStore()
const { notifications } = storeToRefs(notificationStore)
const { isActive: isSystemNotificationFlowActive } = storeToRefs(useSystemNotificationFlowStore())
// endregion

// #region - Method definitions
const updateSystemNotificationApproval = () => {
  const approvalStatus = notifications.value.length === 0
  notificationPermissionStore.updateSystemNotificationApproval(approvalStatus)
}

const toastNotifications = () => {
  return notifications.value.filter(notification => {
    if (notification.page_url) {
      return (
        typeof notification.kind === 'undefined' ||
        (notification.kind === defaultNotificationType && route.path.includes(notification.page_url))
      )
    } else {
      return typeof notification.kind === 'undefined' || notification.kind === defaultNotificationType
    }
  })
}
// Modal system notifications without a page restriction are shown by SystemNotificationFlow;
// page specific system notifications wait until the flow is closed so the modals do not stack.
const singleNotifications = () =>
  notifications.value.filter(
    notification =>
      !isSystemNotificationFlowItem(notification) &&
      !(notification.is_system_notification && isSystemNotificationFlowActive.value)
  )

const modalNotifications = () => {
  updateSystemNotificationApproval()

  return singleNotifications().filter(notification => {
    if (notification.page_url) {
      return notification.kind === NOTIFICATION_TYPES.MODAL && route.path.includes(notification.page_url)
    } else {
      return notification.kind === NOTIFICATION_TYPES.MODAL
    }
  })
}
const refreshNotifications = () => {
  return singleNotifications().filter(notification => {
    if (notification.page_url) {
      return notification.kind === NOTIFICATION_TYPES.REFRESH && route.path.includes(notification.page_url)
    } else {
      return notification.kind === NOTIFICATION_TYPES.REFRESH
    }
  })
}
// endregion
</script>

<style scoped>
.notification {
  .notification-container {
    position: fixed;
    bottom: 20px;
    right: 40px;
    z-index: 9999;

    @media (max-width: 768px) {
      top: 18px;
      right: 18px;
      bottom: unset;
    }
  }
}
</style>
